import React, { useCallback, useEffect, useRef, useState } from 'react';
import { authToken } from '../../api/apiBase';
import {
  PhasesRecoveryStatus, phasesRecoveryFailure, phasesRecoveryOwnerAuthorized, readPhasesRecovery, submitPhasesRecovery,
  PhasesRecoveryTimeoutError,
} from '../../lib/mackPhasesRecovery';
import { PHASES_RETAINED_READ_TIMEOUT_MS } from '../../lib/mackPhasesEvidence';
import { PhasesEvidenceSurface, panelNote, panelTitle } from './primitives';
import { C, MONO, SANS } from './tokens';

const ongoing = new Set(['QUEUED', 'RUNNING', 'RECOVERING']);
const readable = (state: string) => state.replace(/_/g, ' ').toLowerCase();
interface Flight { controller: AbortController; deadline: number }
interface RequestSession { token: string; active: boolean; flight?: Flight }
interface Snapshot { token: string; status: PhasesRecoveryStatus }

export function PhasesHistoryRecovery({ token }: { token?: string | null }) {
  const effectiveToken = token || authToken();
  const session = useRef<RequestSession | null>(null);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [busy, setBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [unconfirmedSubmission, setUnconfirmedSubmission] = useState(false);
  const status = snapshot?.token === effectiveToken ? snapshot.status : null;

  const request = useCallback(async (submit: boolean) => {
    const current = session.current;
    if (!current || !current.active || current.token !== effectiveToken || current.flight) return;
    const controller = new AbortController();
    const isCurrent = () => current.active && session.current === current && current.flight === flight;
    const finish = () => {
      window.clearTimeout(flight.deadline);
      if (isCurrent()) { current.flight = undefined; setBusy(false); setSubmitting(false); }
    };
    const flight: Flight = { controller, deadline: window.setTimeout(() => {
      if (!isCurrent()) return;
      setError(phasesRecoveryFailure(new PhasesRecoveryTimeoutError(), submit));
      finish(); controller.abort();
    }, PHASES_RETAINED_READ_TIMEOUT_MS) };
    current.flight = flight;
    if (submit) setUnconfirmedSubmission(true);
    setBusy(true); setSubmitting(submit); setError('');
    try {
      const next = await (submit ? submitPhasesRecovery : readPhasesRecovery)(current.token, controller.signal);
      if (isCurrent()) { setSnapshot({ token: current.token, status: next }); setUnconfirmedSubmission(false); }
    } catch (failure) {
      if (isCurrent()) {
        // A failed acknowledgement never makes another submission safe by itself.
        setError(phasesRecoveryFailure(failure, submit));
      }
    } finally {
      finish();
    }
  }, [effectiveToken]);

  useEffect(() => {
    const current: RequestSession = { token: effectiveToken, active: true };
    session.current = current;
    setSnapshot(null); setError(''); setUnconfirmedSubmission(false);
    void request(false);
    return () => {
      current.active = false;
      if (current.flight) { window.clearTimeout(current.flight.deadline); current.flight.controller.abort(); }
    };
  }, [effectiveToken, request]);

  const operationState = status?.operation?.state;
  useEffect(() => {
    if (busy || error || !operationState || !ongoing.has(operationState)) return;
    const timer = window.setTimeout(() => { void request(false); }, 4000);
    return () => window.clearTimeout(timer);
  }, [busy, error, operationState, request, snapshot]);

  const ownerAuthorized = status ? phasesRecoveryOwnerAuthorized(status) : false;
  const canSubmit = !busy && !error && !unconfirmedSubmission && status?.can_submit === true && !status.operation && ownerAuthorized;
  const lastConfirmed = !!status && (busy || !!error || unconfirmedSubmission);
  const claimRows = status?.operation?.claims || status?.claims || [];
  const buttonStyle: React.CSSProperties = {
    border: `1px solid ${C.chipOffBd}`, borderRadius: 8, background: C.chipOffBg,
    color: C.text, padding: '8px 12px', fontFamily: SANS, fontSize: 13, cursor: 'pointer',
  };
  return <PhasesEvidenceSurface>
    <h2 style={{ ...panelTitle, margin: 0 }}>Recovery and source-update status</h2>
    <p style={panelNote}>Continue the four authorised history claims once and retain their source evidence. Existing history coverage remains visible separately.</p>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      <button type="button" disabled={!canSubmit} style={{ ...buttonStyle,
        color: canSubmit ? C.amberBright : C.textMuted, cursor: canSubmit ? 'pointer' : 'default' }}
        onClick={() => { if (canSubmit) void request(true); }}>
        {submitting ? 'Submitting continuation…' : 'Resume interrupted raw history'}
      </button>
      <button type="button" disabled={busy} style={buttonStyle} onClick={() => { void request(false); }}>Refresh status</button>
    </div>
    <div aria-live="polite" style={panelNote}>
      {busy && !submitting && <p>Reading recovery status…</p>}
      {error && <p role="alert">{error}</p>}
      {lastConfirmed && <p>Showing the last confirmed recovery status. The current request has not established a newer outcome.</p>}
      {status && <>
        <p>{ownerAuthorized ? `${lastConfirmed ? 'Last confirmed: ' : ''}Owner continuation permission verified.`
          : status.access ? 'This session does not have owner continuation permission.' : 'Owner continuation permission has not been verified.'}</p>
        <p>{status.authority_state === 'VERIFIED' ? 'Installed authority verified.' : `Authority: ${readable(status.authority_state)}.`}
          {' '}{status.resident_available ? 'Resident continuation available.' : 'Resident continuation unavailable.'}</p>
        <p>{status.operation ? `${lastConfirmed ? 'Last confirmed: ' : ''}Continuation ${readable(status.operation.state)}.`
          : unconfirmedSubmission ? 'A continuation acknowledgement is unconfirmed. Refresh status; do not submit again.'
            : `${lastConfirmed ? 'Last confirmed: ' : ''}No continuation request submitted. This recovery has not started.`}</p>
        {status.operation?.state === 'QUEUED' && <p>Request accepted and queued for the resident scheduler. Acquisition completion has not been established.</p>}
        {status.operation?.state === 'COMPLETE' && <p>Terminal continuation receipts are available. History coverage is assessed separately.</p>}
        {status.operation && ['PARTIAL', 'FAILED', 'STORE_PARITY_FAILED'].includes(status.operation.state) &&
          <p>Partial or failed outcome. Inspect the per-claim stage and retained receipts; no automatic resubmission is offered.</p>}
        {!status.operation && status.can_submit && ownerAuthorized && !busy && !error && !unconfirmedSubmission &&
          <p>The four original claims are ready for an owner continuation request.</p>}
        {!status.operation && (!status.can_submit || !!error || unconfirmedSubmission) && <p>Continuation is currently withheld.</p>}
        {status.operation && <p style={{ fontFamily: MONO, overflowWrap: 'anywhere' }}>Recovery receipt: {status.operation.request_id}</p>}
        {status.operation && <p>Accepted: {status.operation.requested_at} · Started: {status.operation.started_at || 'Not started'} · Ended: {status.operation.ended_at || 'Not terminal'}</p>}
        {claimRows.length > 0 && <ul className="phases-recovery-claims">
          {claimRows.map(claim => <li key={claim.claim_identity} style={{ marginTop: 7 }}>
            <span style={{ color: C.text }}>{claim.label}</span>{' · '}{readable(claim.state)}
            {claim.route_state && claim.route_state !== claim.state && <> · {readable(claim.route_state)}</>}
            <span style={{ display: 'block' }}>Recorded source requests: {claim.source_requests}</span>
            {claim.counts_evidence && claim.persistence_counts ? <span style={{ display: 'block' }}>
              {Object.entries(claim.persistence_counts).map(([name, count]) => `${readable(name)}: ${count}`).join(' · ')}
            </span> : <span style={{ display: 'block' }}>Admission, deduplication and revision counts: no continuation persistence receipt projected.</span>}
            {claim.reason && <span style={{ display: 'block' }}>Recorded outcome code: {claim.reason}</span>}
            {claim.response_references?.map((response, index) => <span key={index} style={{ display: 'block' }}>
              Source response: {response.http_status === null || response.http_status === undefined
                ? 'Numeric HTTP status was not retained' : `HTTP ${response.http_status}`}
              {response.error ? ` · Recorded error code: ${response.error}` : ''}
            </span>)}
            {claim.persistence_complete === true && <span style={{ display: 'block' }}>Required persistence complete.</span>}
            {claim.original_source_outcome && <span style={{ display: 'block' }}>Original interrupted response: {readable(claim.original_source_outcome)}</span>}
            {claim.continuation_response_state && <span style={{ display: 'block' }}>Continuation response: {readable(claim.continuation_response_state)}</span>}
          </li>)}
        </ul>}
        <details><summary>Original claims, callback lineage and recovery receipts</summary>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 11 }}>{JSON.stringify(status, null, 2)}</pre>
        </details>
      </>}
    </div>
  </PhasesEvidenceSurface>;
}
